import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
  useContext,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { useAuth } from "@/contexts/AuthContext";
import {
  getMyTenants,
  selectTenant,
  type TenantOption,
} from "@/services/authService";

export interface ActiveTenant {
  id: string;
  name: string | null;
}

interface TenantSwitcherContextValue {
  activeTenant: ActiveTenant | null;
  loadingTenant: boolean;
  tenants: TenantOption[];
  loadingTenants: boolean;
  isSwitchingTenant: boolean;
  switchTenant: (
    tenantId: string,
    options?: { silent?: boolean },
  ) => Promise<void>;
  /** Impersonate a tenant (root only). Shows loader overlay and success toast. */
  impersonate: (tenantId: string, tenantName?: string) => Promise<void>;
  /** End impersonation and return to platform (root only). Shows loader overlay. */
  endImpersonation: () => Promise<void>;
}

const TenantSwitcherContext = createContext<
  TenantSwitcherContextValue | undefined
>(undefined);

export function TenantSwitcherProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const { t } = useTranslation();
  const {
    user,
    isLoading,
    applySelectTenantResponse,
    impersonate: doImpersonate,
    endImpersonation: doEndImpersonation,
  } = useAuth();
  const queryClient = useQueryClient();
  const [isSwitchingTenant, setIsSwitchingTenant] = useState(false);
  const location = useLocation();
  const navigateRouter = useNavigate();
  const [urlInitDone, setUrlInitDone] = useState(false);

  const activeTenant: ActiveTenant | null = useMemo(() => {
    if (!user?.tenantId) return null;
    return {
      id: user.tenantId,
      name: user.tenantName,
    };
  }, [user?.tenantId, user?.tenantName]);

  const {
    data: tenants = [],
    isLoading: loadingTenants,
  } = useQuery({
    queryKey: ["myTenants", user?.id],
    queryFn: getMyTenants,
    enabled: Boolean(user?.tenantId != null),
  });

  const switchTenant = useCallback(
    async (
      tenantId: string,
      options?: { silent?: boolean },
    ): Promise<void> => {
      setIsSwitchingTenant(true);
      try {
        const response = await selectTenant(tenantId);
        // Wipe cache BEFORE flipping the user/session state. Order
        // matters: applySelectTenantResponse awaits getSession, and
        // React can re-render between that promise resolving and the
        // next line. If removeQueries ran after, the queries gated on
        // sessionMatches would briefly enable with the previous tenant
        // still cached under shared keys (tenantKeys.current(),
        // dashboardKeys.metrics()), causing the wrong tenant's data
        // to flash into accountant flows. Clearing first ensures the
        // re-render sees an empty cache and lands on its LoadingPage.
        queryClient.removeQueries();
        await applySelectTenantResponse(response);
        if (!options?.silent) {
          const tenantName = response.user.tenantName ?? "";
          toast.success(t("header.switchedToTenant", { tenantName }));
        }
      } finally {
        setIsSwitchingTenant(false);
      }
    },
    [applySelectTenantResponse, queryClient, t]
  );

  // Initial mount sync: URL → session.
  // If the URL carries ?tenant=X and the session's active tenant is different
  // (refresh on a deep link, paste into a fresh tab), silently switch the
  // session to match the URL so the user lands where the URL says.
  useEffect(() => {
    if (urlInitDone) return;
    if (!user?.tenantId) return; // wait for session
    // Platform sessions don't use /auth/select-tenant — the backend rejects
    // it (auth.controller.ts) and root reaches tenants via /auth/impersonate
    // instead. Mark URL-init done so the gate below never blocks root.
    if (user.platformRole === "root" || user.platformRole === "root_admin") {
      setUrlInitDone(true);
      return;
    }
    if (location.pathname.startsWith("/accountant")) {
      setUrlInitDone(true);
      return;
    }
    const urlTenant = new URLSearchParams(location.search).get("tenant");
    if (!urlTenant || urlTenant === user.tenantId) {
      setUrlInitDone(true);
      return;
    }
    void (async () => {
      try {
        const response = await selectTenant(urlTenant);
        await applySelectTenantResponse(response);
        // See switchTenant for why removeQueries (drop) beats
        // invalidateQueries (mark stale) here — children mount under the
        // overlay below and we want them to see an empty cache, not the
        // previous tenant's data through stale-while-revalidate.
        queryClient.removeQueries();
      } catch {
        // Bad / forbidden tenant in URL — the session→URL effect will
        // rewrite the param to the current active tenant once we unblock.
      } finally {
        setUrlInitDone(true);
      }
    })();
  }, [
    urlInitDone,
    user?.tenantId,
    location.pathname,
    location.search,
    applySelectTenantResponse,
    queryClient,
  ]);

  // Session → URL sync: only meaningful for users who actually navigate
  // across multiple tenants — i.e. accountants viewing a client's data,
  // or root/root_admin impersonating tenants. A regular tenant user
  // (single-tenant owner/admin/employee) doesn't need ?tenant=ID in
  // their URL: the session already pins them to their one tenant, the
  // param just exposes a UUID. For those users we proactively strip it
  // out of the URL if something added it earlier (deep link, manual
  // edit, accountant share).
  const hasAccountantMembership = useMemo(
    () => tenants.some((t) => t.role === "accountant"),
    [tenants],
  );
  const wantsTenantInUrl =
    user?.platformRole === "root" ||
    user?.platformRole === "root_admin" ||
    hasAccountantMembership;

  useEffect(() => {
    if (!urlInitDone) return;
    if (!user?.tenantId) return;
    if (location.pathname.startsWith("/accountant")) return;
    const params = new URLSearchParams(location.search);
    const urlTenant = params.get("tenant");

    if (wantsTenantInUrl) {
      if (urlTenant === user.tenantId) return;
      params.set("tenant", user.tenantId);
      navigateRouter(
        { pathname: location.pathname, search: params.toString() },
        { replace: true },
      );
      return;
    }

    // Tenant user — strip the param if it leaked in (e.g. via an
    // accountant-shared link or an older session that added it).
    if (urlTenant != null) {
      params.delete("tenant");
      navigateRouter(
        {
          pathname: location.pathname,
          search: params.toString() ? params.toString() : "",
        },
        { replace: true },
      );
    }
  }, [
    urlInitDone,
    user?.tenantId,
    user?.platformRole,
    wantsTenantInUrl,
    location.pathname,
    location.search,
    navigateRouter,
  ]);

  const impersonateWithLoader = useCallback(
    async (tenantId: string, tenantName?: string): Promise<void> => {
      setIsSwitchingTenant(true);
      try {
        await doImpersonate(tenantId);
        const name = tenantName?.trim() ?? "";
        toast.success(
          name
            ? t("header.switchedToTenant", { tenantName: name })
            : t("header.switchedToBusiness")
        );
      } finally {
        setIsSwitchingTenant(false);
      }
    },
    [doImpersonate, t]
  );

  const endImpersonationWithLoader = useCallback(async (): Promise<void> => {
    setIsSwitchingTenant(true);
    try {
      await doEndImpersonation();
      toast.success(t("header.returnedToCompanies"));
    } finally {
      setIsSwitchingTenant(false);
    }
  }, [doEndImpersonation, t]);

  const value = useMemo<TenantSwitcherContextValue>(
    () => ({
      activeTenant,
      loadingTenant: isLoading,
      tenants,
      loadingTenants,
      isSwitchingTenant,
      switchTenant,
      impersonate: impersonateWithLoader,
      endImpersonation: endImpersonationWithLoader,
    }),
    [
      activeTenant,
      isLoading,
      tenants,
      loadingTenants,
      isSwitchingTenant,
      switchTenant,
      impersonateWithLoader,
      endImpersonationWithLoader,
    ]
  );

  // Deep-link refresh case: URL says ?tenant=X but the session is still on
  // a different tenant. Without this gate the destination page mounts and
  // fetches with the previous session for one frame before the URL-init
  // effect can swap. Block children synchronously so the dashboard never
  // sees the wrong tenant. Skip for platform sessions: root uses
  // /auth/impersonate (which the impersonateWithLoader overlay covers
  // already), not /auth/select-tenant, so the gate would just hang on a
  // 403'd swap.
  const isPlatformSession =
    user?.platformRole === "root" || user?.platformRole === "root_admin";
  const urlTenantParam = new URLSearchParams(location.search).get("tenant");
  const needsUrlSwap =
    !urlInitDone &&
    !isPlatformSession &&
    user?.tenantId != null &&
    !location.pathname.startsWith("/accountant") &&
    urlTenantParam != null &&
    urlTenantParam !== user.tenantId;

  return (
    <TenantSwitcherContext.Provider value={value}>
      {needsUrlSwap ? null : children}
      {needsUrlSwap || isSwitchingTenant ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm"
          role="status"
          aria-live="polite"
          aria-label="Switching business"
        >
          <LoadingLogo size="lg" />
        </div>
      ) : null}
    </TenantSwitcherContext.Provider>
  );
}

export function useTenantSwitcher(): TenantSwitcherContextValue {
  const context = useContext(TenantSwitcherContext);
  if (context === undefined) {
    throw new Error(
      "useTenantSwitcher must be used within a TenantSwitcherProvider"
    );
  }
  return context;
}
